import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './lib/auth';
import { FeaturesProvider, useFeatures } from './lib/features';
import { NetworkProvider } from './lib/networkStore';
import { ApptProvider } from './lib/apptStore';
import { Toaster } from './components/ui/sonner';

import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import AppLayout from './components/AppLayout';
import Worklist from './pages/Worklist';
import Appointments from './pages/Appointments';
import AppointmentDetail from './pages/AppointmentDetail';
import Eligibility from './pages/Eligibility';
import NewEligibility from './pages/NewEligibility';
import VerificationDetail from './pages/VerificationDetail';
import CommThread from './pages/CommThread';
import AgentActivity from './pages/AgentActivity';
import Policies from './pages/Policies';
import Finance from './pages/Finance';
import Patients from './pages/Patients';
import Network from './pages/Network';
import Contracts from './pages/Contracts';
import ContractDetail from './pages/ContractDetail';
import Estimator from './pages/Estimator';
import Insights from './pages/Insights';
import Settings from './pages/Settings';
// Claims module removed — denial intelligence lives in Insights (paid-first V1).

function RequireAuth({ children }) {
  const { session, loading } = useAuth();
  if (loading) return null;
  if (!session) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { session, loading } = useAuth();
  if (loading) return null;
  if (session) return <Navigate to="/app" replace />;
  return children;
}

// Gate a route behind a feature flag — redirects to the worklist when off.
function FeatureGate({ flag, children }) {
  const { features } = useFeatures();
  if (!features[flag]) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FeaturesProvider>
        <NetworkProvider>
        <ApptProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/app" element={<RequireAuth><AppLayout /></RequireAuth>}>
              <Route index element={<Worklist />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="appointments/:id" element={<AppointmentDetail />} />
              <Route path="eligibility" element={<Eligibility />} />
              <Route path="eligibility/new" element={<NewEligibility />} />
              <Route path="eligibility/:id" element={<VerificationDetail />} />
              <Route path="verify/:id" element={<VerificationDetail />} />
              <Route path="comms/:id" element={<CommThread />} />
              <Route path="agents" element={<AgentActivity />} />
              <Route path="policies" element={<Policies />} />
              <Route path="finance" element={<Finance />} />
              <Route path="patients" element={<Patients />} />
              <Route path="network" element={<Network />} />
              <Route path="contracts" element={<Contracts />} />
              <Route path="contracts/:id" element={<ContractDetail />} />
              <Route path="estimator" element={<FeatureGate flag="estimator"><Estimator /></FeatureGate>} />
              <Route path="insights" element={<Insights />} />
              <Route path="settings" element={<Settings />} />
              {/* legacy redirects — Workflows folded into the appointment screen */}
              <Route path="dashboard" element={<Navigate to="/app" replace />} />
              <Route path="tasks" element={<Navigate to="/app" replace />} />
              <Route path="workflows" element={<Navigate to="/app" replace />} />
              <Route path="workflows/:queue" element={<Navigate to="/app" replace />} />
              <Route path="claims" element={<Navigate to="/app/insights" replace />} />
              <Route path="metrics" element={<Navigate to="/app/insights" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        </ApptProvider>
        </NetworkProvider>
        </FeaturesProvider>
      </AuthProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
